import { convertSeriesBasis } from '../hooks/seriesMath';
import { parseDateInput } from './customRange';

/**
 * Growth and net-margin helpers for the Scores card (the Fundamentals card
 * these also fed was deleted; see SCORE_CARDS.md §5).
 *
 * Outputs are fractions (0.10 = 10%). The chart multiplies by 100.
 *
 * A denominator the ratio math already refuses (`buildRatioSeries`: null,
 * non-finite, or exactly 0) is "essentially zero". A negative prior year is
 * not: growth divides by the absolute value, so a loss year still produces
 * a point and the sign comes from the numerator. There is no extra epsilon
 * beyond that exact-zero check.
 */

export const isEssentiallyZero = (value) => (
  value == null || typeof value !== 'number' || !Number.isFinite(value) || value === 0
);

/**
 * Year-over-year growth `(current − prior) / |prior|`.
 * Returns null (no point, not 0) when the prior year is missing or ~0,
 * or when the current value is missing.
 */
export const yoyGrowth = (current, prior) => {
  if (current == null || typeof current !== 'number' || !Number.isFinite(current)) return null;
  if (isEssentiallyZero(prior)) return null;
  return (current - prior) / Math.abs(prior);
};

const utcYear = (ts) => new Date(ts).getUTCFullYear();

/**
 * Growth points for an already-annual `[timestamp, value][]` series.
 * A point is emitted only when the previous point is the prior fiscal year
 * (UTC year − 1) and that prior value is a usable denominator. Missing
 * years and ~0 priors are omitted, not drawn as 0.
 */
export const yoySeries = (series = []) => {
  const points = [];
  (series || []).forEach((point) => {
    if (!point || point[0] == null) return;
    points.push(point);
  });
  const out = [];
  for (let i = 1; i < points.length; i += 1) {
    const [t, value] = points[i];
    const [prevT, prior] = points[i - 1];
    if (utcYear(t) !== utcYear(prevT) + 1) continue;
    const growth = yoyGrowth(value, prior);
    if (growth == null) continue;
    out.push([t, growth]);
  }
  return out;
};

/** Drop timestamps after the last real price date. No-op when that date is unknown. */
export const dropAfterLastPrice = (series, lastPriceTs) => {
  if (!series?.length) return [];
  if (lastPriceTs == null) return series.filter((point) => point && point[0] != null);
  return series.filter((point) => point && point[0] != null && point[0] <= lastPriceTs);
};

/**
 * Convert a total-native series onto the page display scale.
 * Non-finite results (missing shares) become nulls rather than 0.
 */
export const scaleTotalSeries = (series, displayScale, shares) => {
  const converted = convertSeriesBasis(series || [], 'total', displayScale, shares || []);
  return converted.map(([t, value]) => (
    [t, typeof value === 'number' && Number.isFinite(value) ? value : null]
  ));
};

/**
 * Revenue (or any total-native annual series) growth on the page display
 * scale. Per share grows the per-share series; total grows the company total.
 */
export const growthOnDisplayScale = (series, shares, displayScale, lastPriceTs = null) => {
  const filtered = dropAfterLastPrice(series, lastPriceTs);
  const scaled = scaleTotalSeries(filtered, displayScale, shares);
  return yoySeries(scaled);
};

export const statementTimestamp = (row) => {
  if (!row) return null;
  if (typeof row.date === 'number' && Number.isFinite(row.date)) return row.date;
  if (typeof row.date === 'string' && row.date) return parseDateInput(row.date.slice(0, 10));
  return null;
};

export const fiscalYearNumber = (row, ts) => {
  const raw = row?.fiscalYear;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  if (ts == null) return null;
  return utcYear(ts);
};

/**
 * Net margin = annual net income / revenue. Gap (omit) the year when revenue
 * is missing or not positive, or net income is missing. Ignores value basis
 * and display scale — those are not inputs.
 */
export const netMarginFromRow = (row) => {
  if (!row) return null;
  const { revenue, netIncome } = row;
  if (typeof revenue !== 'number' || !Number.isFinite(revenue) || !(revenue > 0)) return null;
  if (typeof netIncome !== 'number' || !Number.isFinite(netIncome)) return null;
  return netIncome / revenue;
};

/**
 * Reported fiscal years from `incomeStatement.annual`, oldest first.
 * One point per fiscal year. Points after the last real price date are dropped.
 */
export const reportedFiscalYears = (annualRows, lastPriceTs) => {
  const byYear = new Map();
  (annualRows || []).forEach((row) => {
    const x = statementTimestamp(row);
    if (x == null) return;
    if (lastPriceTs != null && x > lastPriceTs) return;
    const year = fiscalYearNumber(row, x);
    if (year == null) return;
    const existing = byYear.get(year);
    if (!existing || x > existing.x) byYear.set(year, { year, x, row });
  });
  return [...byYear.values()].sort((a, b) => a.year - b.year);
};

const FORWARD_MIN_GAP_MS = 270 * 24 * 60 * 60 * 1000;
const FORWARD_MAX_GAP_MS = 450 * 24 * 60 * 60 * 1000;

/**
 * Forward fiscal years from a `metrics.*` series' estimate tail: the points
 * strictly after the last real price date, numbered by ordinal continuation
 * from the last reported fiscal year. The TTM bridge is dated at a past
 * quarter end, so the price filter already excludes it.
 *
 * Numbering is deliberately NOT the point's UTC year: for a fiscal year ending
 * in January or February the UTC year disagrees with the provider's
 * `fiscalYear` label, which would make a forward point collide with (and, in
 * `toPercentPoints`' year-keyed map, overwrite) a reported year. Each step must
 * also be one period away (270–450 days) from the previous one, so a gap in the
 * estimate tail ends the spine instead of silently chaining non-adjacent years.
 */
export const forwardFiscalYears = (series, lastPriceTs, lastReported) => {
  if (lastPriceTs == null || lastReported?.year == null || lastReported?.x == null) return [];
  const ahead = (series || [])
    .filter((point) => point && point[0] != null && point[0] > lastPriceTs)
    .map((point) => point[0])
    .sort((a, b) => a - b);
  const out = [];
  let prevX = lastReported.x;
  let prevYear = lastReported.year;
  for (let i = 0; i < ahead.length; i += 1) {
    const gap = ahead[i] - prevX;
    if (gap > FORWARD_MAX_GAP_MS) break;
    if (gap < FORWARD_MIN_GAP_MS) continue;
    prevX = ahead[i];
    prevYear += 1;
    out.push({ year: prevYear, x: prevX });
  }
  return out;
};
